import { HOST_ELEMENT_KEY, registerNewDirective, setupPrototype, verifyAaSelector as verifyAaSelector } from './renderer';

export interface DomDirectiveConfig {
  selector: string;
}

/**
 * Directives allow you to add functionallity to existing DOM elements, like
 * a form or input element. Directives will connect themselves to these elements.
 * In contrast with Components Directives do not have support for templating.
 *
 * Take a look at the following example of a Directive which can be used to focus
 * on an existing `<input>` field:
 *
 * ```typescript
 * // Define the InputFocus class as a Directive
 * // which allows us to add it as an attribute to our input field (aa-input-focus)
 * @DomDirective({
 *  selector: 'aa-input-focus'
 * })
 * class InputFocus implements OnConnect {
 *  // The {@link @HostElement} binds the connected element to the given property.
 *  @HostElement
 *  private readonly inputElm: HTMLInputElement;
 *
 *  // Focus when connected
 *  connected() {
 *    this.inputElm.focus();
 *  }
 * }
 * ```
 *
 * Using the {@link @HostElement} decorator we're retrieving a reference to the bound
 * DOM element, which in our case is the `<input>` element.
 *
 * Implementing the {@link OnConnect} interface and defining the {@link connected} method allows
 * us to specify the code which should execute after the directive has been connected. In our
 * case we want to put focus on the directive's element.
 *
 * @param config
 */
export const DomDirective = (config: DomDirectiveConfig) => (cls: any) => {
  verifyAaSelector(config.selector);
  cls = setupPrototype(cls);

  // Register new elements
  registerNewDirective(`[${config.selector}]`, cls);

  return cls;
};

export const HostElement = () => {
  return function (target: any, propertyKey: string | symbol): any {
    return {
      enumerable: false,
      configurable: false,
      get: function () {
        return this[HOST_ELEMENT_KEY]();
      },
    };
  };
};
